import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import _ from 'lodash';

import { routes, TEMPLATE_TYPE } from 'src/app/constants';
import { ListQueryResult, Template } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';

import { ComposePage } from '../compose/compose.page';
import { BLOCK_TYPES, htmlToBlocks, renderBlocks } from '../slack-blocks/common';
import { UnlayerWrapperComponent } from '../unlayer-wrapper/unlayer-wrapper.component';

import { TemplateOptionsPage } from './template-options/template-options.page';

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
})
export class TemplatesComponent implements OnInit {
  @Input() communityId: string | null = null;
  @ViewChild(UnlayerWrapperComponent) emailEditor: UnlayerWrapperComponent;

  myTemplates: Template[];
  sampleTemplates: Template[];
  blocks: any[] = [];

  template?: Partial<Template>;
  isCreating = false;
  isLoading = false;
  isReadOnly = false;
  isFetchingMembers = false;
  warning: string;

  constructor(
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private navCtrl: NavController,
    public authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private customersService: CustomersService,
  ) {}

  ngOnInit() {
    this.getList();

    this.route.queryParams.subscribe((params) => {
      const hasEditParam = params.edit;

      if (this.template && !hasEditParam) {
        this.checkIfShouldGoBack();
      }

      if (hasEditParam?.length) {
        this.selectTemplate(this.myTemplates.find((t) => t.id === hasEditParam));
      } else if (this.isCreating) {
        this.goBack();
      }
    });
  }

  editorLoaded() {
    setTimeout(() => {
      if (this.template && !_.isEmpty(this.template.design)) {
        this.emailEditor.editor.loadDesign(this.template.design);
      } else {
        this.emailEditor.editor.loadBlank({
          backgroundColor: '#fff',
          contentWidth: '650px',
        });
        // Hacky shit because this editor sucks
        // If we dont' have this timeout, it will not work
        setTimeout(() => {
          if (!this.template || !this.template.id) {
            this.emailEditor.editor.setBodyValues({
              backgroundColor: '#fff',
              contentWidth: '650px',
            });
          }
        }, 3000);
      }
    }, 500);

    this.emailEditor.editor.addEventListener('design:updated', (data) => {
      // This is called when the editor changes
      // We've had issues with people embedding iFrames, which don't appear properly in email
      // This code is semi-problematic because it will clear out the warning when you edit a different HTML element,
      // but it's good enough for now
      if (
        _.get(data, 'item.type') === 'html' &&
        _.get(data, 'item.values.html').toLowerCase().indexOf('<iframe') >= 0
      ) {
        this.warning = 'You have embedded an iFrame as HTML. This will not appear when sent via email.';
      } else {
        this.warning = '';
      }
    });
  }

  async selectTemplate(template?: Partial<Template>) {
    this.template = template;
    this.isCreating = true;
    const isUsingSample = !this.template.tenantId;

    if (!this.template) {
      return;
    }

    if (isUsingSample) {
      this.template.name = `Copy of ${this.template.name}`;
      this.template.tenantId = this.authService.tenantId;
      this.template.projectId = this.authService.selectedProject?.id;
      this.template.id = undefined;
    }

    this.analyticsService.trackEvent(
      'Template Editor',
      `Select ${template.tenantId ? 'existing' : 'Unthread-provided'} template`,
    );

    if (this.template.projectId) {
      const project: any = await this.apiService.getPromise(`/projects/${this.template.projectId}`);

      this.template.project = project;
    }

    if (template.type === TEMPLATE_TYPE.SLACK) {
      this.blocks = htmlToBlocks(template.html ?? 'Sample content here');

      if (!this.blocks.length) {
        this.blocks.push({
          type: BLOCK_TYPES.RICH_TEXT,
          htmlContent: 'Sample content here',
        });
      }
    } else if (template.type === TEMPLATE_TYPE.EMAIL) {
      if (!_.isEmpty(this.template.design)) {
        this.emailEditor.editor.loadDesign(this.template.design);
      } else {
        this.emailEditor.editor.loadBlank({
          backgroundColor: '#fff',
          contentWidth: '650px',
        });
        this.emailEditor.editor.setBodyValues({
          backgroundColor: '#fff',
          contentWidth: '650px',
        });
      }
    }

    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: template?.id ? { edit: template?.id } : {},
      queryParamsHandling: 'merge',
      skipLocationChange: false,
    });
  }

  async newEmailTemplate() {
    if (this.authService.selectedProject) {
      this.selectTemplate({
        name: 'My email template',
        isDiscoverable: true,
        type: TEMPLATE_TYPE.EMAIL,
        projectId: this.authService.selectedProject.id,
      });
      return;
    }

    const modal = await this.modalCtrl.create({
      component: TemplateOptionsPage,
      cssClass: 'modal-content-only modal-skinny',
      componentProps: {
        template: {
          name: 'My email template',
          isDiscoverable: true,
          type: TEMPLATE_TYPE.EMAIL,
          tenantId: this.authService.tenantId,
        },
        isNewTemplate: true,
        callback: (template) => {
          modal.dismiss();
          if (template) {
            this.selectTemplate(template);
          }
        },
      },
    });
    await modal.present();
  }

  async newSlackTemplate() {
    if (this.authService.selectedProject) {
      this.selectTemplate({
        name: 'My Slack template',
        isDiscoverable: true,
        type: TEMPLATE_TYPE.SLACK,
        projectId: this.authService.selectedProject.id,
      });
      return;
    }

    const modal = await this.modalCtrl.create({
      component: TemplateOptionsPage,
      cssClass: 'modal-content-only modal-skinny',
      componentProps: {
        template: {
          name: 'My Slack template',
          isDiscoverable: true,
          type: TEMPLATE_TYPE.SLACK,
          tenantId: this.authService.tenantId,
        },
        isNewTemplate: true,
        callback: (template) => {
          modal.dismiss();
          if (template) {
            this.selectTemplate(template);
          }
        },
      },
    });
    await modal.present();
  }

  async editTemplate() {
    if (this.isReadOnly) {
      return;
    }

    this.presentEditModalWithProps({
      template: this.template,
    });
  }

  async clone() {
    this.presentEditModalWithProps({
      template: this.template,
      clone: true,
    });

    this.analyticsService.trackEvent(
      'Template Editor',
      `Clone ${this.template?.tenantId ? 'existing' : 'Unthread-provided'} template`,
    );
  }

  async showWarnings() {
    const alert = await this.alertCtrl.create({
      header: 'Warnings',
      subHeader: this.warning,
      buttons: [
        {
          text: 'OK',
        },
      ],
    });
    alert.present();
  }

  async sendEmail() {
    this.analyticsService.trackEvent('Template Editor', 'Send email from template editor');

    // Get member list to populate
    let emails = [];

    if (this.communityId) {
      this.isFetchingMembers = true;

      const members = await this.customersService.getFollowers(this.communityId);

      emails = members.map((member) => {
        return member.user?.email;
      });

      this.isFetchingMembers = false;
    }

    this.emailEditor.editor.exportHtml(async (data) => {
      const { html } = data;
      const modal = await this.modalCtrl.create({
        component: ComposePage,
        componentProps: {
          communityId: this.communityId,
          emails,
          // Kinda hacky way of pretending this is an Outbound object
          draft: {
            body: html,
            communityId: this.communityId,
            recipients: [],
            metadata: {
              templateId: this.template.id,
            },
          },
        },
        backdropDismiss: false,
        cssClass: 'modal-large',
      });
      modal.present();
    });
  }

  async sendSlack() {
    this.analyticsService.trackEvent('Template Editor', 'Send Slack from template editor');

    this.navCtrl.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: {
        communityId: this.communityId,
        templateId: this.template.id,
      },
    });
  }

  async presentEditModalWithProps(props: any) {
    const modal = await this.modalCtrl.create({
      component: TemplateOptionsPage,
      componentProps: {
        ...props,
        communityId: this.communityId,
        callback: (template) => {
          modal.dismiss();
          this.getList();
          if (template) {
            this.template.name = template.name;
            this.template.isDiscoverable = template.isDiscoverable;
            this.template.projectId = template.projectId;
            this.template.project = template.project;

            if (props.clone) {
              // After a clone, we'll want to save immediately because the user has entered a name and design
              this.save();
            }
          } else {
            this.goBack();
          }
        },
      },
      cssClass: 'modal-content-only modal-skinny',
    });
    modal.present();
  }

  async checkIfShouldGoBack() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to go back?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: () => {
            this.goBack();
          },
        },
      ],
    });

    alert.present();
  }

  async getList() {
    const params = this.authService.isTenantAdmin
      ? this.authService.selectedProject
        ? {
            projectId: this.authService.selectedProject.id,
          }
        : {}
      : {
          projectId: this.authService.projects
            .filter(
              (p) =>
                this.authService.isProjectOwner(p.id) &&
                (!this.authService.selectedProject || p.id === this.authService.selectedProject.id),
            )
            .map((p) => p.id),
        };

    try {
      const result: any =
        !this.authService.selectedProject || this.authService.isProjectOwner(this.authService.selectedProject.id)
          ? await this.apiService.getPromise(`/templates`, params)
          : {
              rows: [],
            };

      this.myTemplates = result.rows;
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }

    const samples: ListQueryResult = await this.apiService.getPromise(`/template-samples`);
    this.sampleTemplates = samples.rows;
  }

  async save() {
    if (this.template.type === TEMPLATE_TYPE.SLACK) {
      if (this.template.id) {
        this.updateTemplateToServer(this.blocks, null, renderBlocks(this.blocks));
      } else {
        this.analyticsService.trackEvent('Template editor', 'Save new Slack template');
        this.saveNewTemplateToServer(this.blocks, null, renderBlocks(this.blocks));
      }
    } else if (this.template.type === TEMPLATE_TYPE.EMAIL) {
      this.emailEditor.editor.exportHtml(async (data) => {
        const { chunks, design, html } = data;

        const cleanedHtml = html.replace('<head>', '').replace('</head>', '');
        this.isLoading = true;

        if (this.template.id) {
          this.updateTemplateToServer(chunks, design, cleanedHtml);
        } else {
          this.analyticsService.trackEvent('Template editor', 'Save new email template');
          this.saveNewTemplateToServer(chunks, design, cleanedHtml);
        }
      });
    }
  }

  async updateTemplateToServer(chunks: any, design = null, html = null) {
    this.isLoading = true;

    try {
      await this.apiService.putPromise(`/templates/${this.template.id}`, {
        type: this.template.type,
        chunks,
        design,
        html,
      });

      this.msgSrvc.show('✔ Nice! Saved.');
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async saveNewTemplateToServer(chunks: any, design = null, html = null) {
    this.isLoading = true;

    const request = this.apiService.post(`/templates`, {
      name: this.template.name,
      type: this.template.type,
      isDiscoverable: this.template.isDiscoverable,
      chunks,
      design,
      html,
      communityId: this.communityId || null,
      projectId: this.template.projectId,
    });

    request.subscribe(
      async (result) => {
        const project = this.template.project;

        this.template = result;
        this.template.project = project;

        this.isReadOnly = false;
        this.msgSrvc.show('✔ Nice! New template saved.');

        this.isLoading = false;
      },
      async (err) => {
        this.msgSrvc.showError(err);

        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  goBack() {
    this.isCreating = false;
    this.template = undefined;

    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        edit: undefined,
      },
      queryParamsHandling: 'merge',
      skipLocationChange: false,
    });

    this.getList();
  }

  unlayerOptions() {
    return {
      fonts: {
        showCustomFonts: false,
        customFonts: [
          {
            label: 'Arial',
            value: 'arial,helvetica,sans-serif',
          },
          {
            label: 'Arial Black',
            value: 'arial black,avant garde,arial',
          },
          {
            label: 'Book Antiqua',
            value: 'book antiqua,palatino',
          },
          {
            label: 'Comic Sans MS',
            value: 'comic sans ms,sans-serif',
          },
          {
            label: 'Courier New',
            value: 'courier new,courier',
          },
          {
            label: 'Georgia',
            value: 'georgia,palatino',
          },
          {
            label: 'Helvetica',
            value: 'helvetica,sans-serif',
          },
          {
            label: 'Impact',
            value: 'impact,chicago',
          },
          {
            label: 'Tahoma',
            value: 'tahoma,arial,helvetica,sans-serif',
          },
          {
            label: 'Times New Roman',
            value: 'times new roman,times',
          },
          {
            label: 'Trebuchet MS',
            value: 'trebuchet ms,geneva',
          },
          {
            label: 'Verdana',
            value: 'verdana,geneva',
          },
        ],
      },
    };
  }

  getProjectName(projectId: string) {
    return this.authService.projects.find((p) => p.id === projectId)?.name;
  }
}
